import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSkillsThread, useSendSkillsMessage, useApplySkillEdit } from "../hooks/useSkills";
import { ChatThread } from "../components/chat/ChatThread";
import { ChatInput } from "../components/chat/ChatInput";
import { SkillEditBanner } from "../components/skills/SkillEditBanner";
import { Button } from "../components/ui/Button";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader";

export function Skills() {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const [appliedEdits, setAppliedEdits] = useState<Set<string>>(new Set());
  const [pendingEdit, setPendingEdit] = useState<{
    slug: string;
    name: string;
    content: string;
  } | null>(null);

  const { data: threadData, isLoading, error } = useSkillsThread(threadId);
  const sendMessage = useSendSkillsMessage(threadId || "");
  const applyEdit = useApplySkillEdit(threadId || "");

  const thread = threadData?.data;
  const messages = thread?.messages ?? [];

  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const isWaitingForAI = lastMessage?.role === "user" || sendMessage.isPending;

  const handleSend = async (content: string) => {
    if (!threadId) return;
    const result = await sendMessage.mutateAsync({ content });
    // Check if the response contains a skill edit suggestion
    if (result.data.skill_edit) {
      setPendingEdit(result.data.skill_edit);
    }
  };

  const handleApplyEdit = async () => {
    if (!pendingEdit || !threadId) return;
    try {
      await applyEdit.mutateAsync({
        skill_slug: pendingEdit.slug,
        skill_name: pendingEdit.name,
        new_content: pendingEdit.content,
      });
      setAppliedEdits((prev) => new Set(prev).add(pendingEdit.slug));
      setPendingEdit(null);
    } catch {
      // Error handled by mutation
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (error || !thread) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center">
        <p className="mb-2 text-sm text-red-600">
          {error ? "Failed to load skills thread." : "Thread not found."}
        </p>
        <Button variant="secondary" size="sm" onClick={() => navigate("/brand")}>
          Back to brand
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col gap-4">
      <div className="shrink-0 px-4 pt-4">
        <WorkspaceHeader
          eyebrow="Operator Workspace"
          title="Skills Coach"
          description="Chat to view, edit, or create marketing skills for your brand."
          breadcrumbs={[
            { label: "Workspace", href: "/home" },
            { label: "Skills" },
          ]}
          actions={(
            <Button variant="secondary" size="sm" onClick={() => navigate("/brand")}>
              Done
            </Button>
          )}
          metadata={(
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              Skill coaching / edits
            </span>
          )}
        />

        {pendingEdit && (
          <SkillEditBanner
            slug={pendingEdit.slug}
            name={pendingEdit.name}
            onApply={handleApplyEdit}
            applying={applyEdit.isPending}
            applied={appliedEdits.has(pendingEdit.slug)}
          />
        )}
      </div>

      <ChatThread messages={messages} isWaiting={isWaitingForAI} />

      <ChatInput onSend={handleSend} disabled={sendMessage.isPending} />
    </div>
  );
}
